import React, { useState, useCallback, useContext, useEffect } from 'react';
import { DataGridWrapper } from '../../../../Utils/DataGridWrapper/DataGridWrapper';
import { GridReactContext } from '../../../../Utils/DataGridWrapper/GridContext';
import { i18n } from '../../../../Utils/i18n/i18n';
import { getPendingApprovalsAPI, approveOrRejectUserUploadAPI } from '../../../../Services/api';
import { format } from 'date-fns';
import { get } from 'lodash';
import './CourseAttendanceTable.scss';
import { SignDocTypes } from '../../../../Components/SignDoc/SignDocLib';
import { SignDocApprovalActions } from '../../../../Components/SignDoc/SignDocApprovalActions/SignDocApprovalActions';
import { PhoneCell } from '../../../../Utils/PhoneCell/PhoneCell';

const tableFilters = {
    showFilters: {
        search: true,
        signDocs: true, 
    },
    filterData: {},
    filterValues: {}
};

const paginationModel = {
    page: 0,
    pageSize: 50
};

const getColumns = ({ handleApprove, handleRejectClick }) => {
    return [
        {
            field: 'userName',
            headerName: i18n.translate('courseAttendance.columns.userName'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                const user = get(params, 'row.user');
                return user ? `${user.firstName} ${user.lastName}` : '';
            }
        },
        {
            field: 'phone',
            headerName: i18n.translate('courseAttendance.columns.phone'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                return get(params, 'row.user.phoneFormatted', '') || get(params, 'row.user.phone', '');
            },
            renderCell: (params) => <PhoneCell value={params.value} showTitle={false} />
        },
        {
            field: 'idNumber',
            headerName: i18n.translate('courseAttendance.columns.idNumber'),
            flex: 1,
            minWidth: 150,
            valueGetter: (params) => {
                return get(params, 'row.user.idNumber', '');
            }
        },
        {
            field: 'courseName',
            headerName: i18n.translate('courseAttendance.columns.courseName'),
            flex: 1,
            minWidth: 200,
            valueGetter: (params) => {
                return get(params, 'row.signDoc.docName', '');
            }
        },
        {
            field: 'courseType',
            headerName: i18n.translate('courseAttendance.columns.courseType'),
            width: 120,
            valueGetter: (params) => {
                const type = get(params, 'row.signDoc.type', '');
                return type === 'FRONTAL_COURSE'
                    ? i18n.translate('courseAttendance.courseTypes.frontal')
                    : i18n.translate('courseAttendance.courseTypes.online');
            }
        },
        {
            field: 'sessionDate',
            headerName: i18n.translate('courseAttendance.columns.sessionDate'),
            width: 150,
            valueGetter: (params) => {
                const sessionStartDate = get(params, 'row.signDoc.additionalData.sessionStartDate');
                if (sessionStartDate) {
                    return format(new Date(sessionStartDate), 'dd/MM/yyyy HH:mm');
                }
                return '';
            }
        },
        {
            field: 'actions',
            headerName: i18n.translate('courseAttendance.columns.actions'),
            width: 150,
            sortable: false,
            renderCell: (params) => {
                const signDocType = get(params, 'row.signDoc.type', '');

                return (
                    <SignDocApprovalActions
                        row={params.row}
                        signDocType={signDocType}
                        isCurrentUserApprover={true} // In this table, user is always an approver
                        onApprove={handleApprove}
                        onReject={handleRejectClick}
                        i18n={i18n}
                    />
                );
            }
        }
    ];
};

const convertData = (data) => {
    return Array.isArray(data) ? data : [];
};

export const CourseAttendanceTable = ({ organizationsOptions, adminOrganizations, signDocsOptions }) => {
    const gridContext = useContext(GridReactContext);
    const [localTableFilters, setLocalTableFilters] = useState(() => {
        return {
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: organizationsOptions,
                signDocTypes: [SignDocTypes.FRONTAL_COURSE, SignDocTypes.ONLINE_COURSE], // Filter to only course types
                signDocs: signDocsOptions
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [get(organizationsOptions, '[0].value', 'all')],
                signDocIds: ['all']
            }
        };
    });

    // Update filterData when signDocsOptions changes
    useEffect(() => {
        if (signDocsOptions && signDocsOptions.length > 0) {
            setLocalTableFilters(prevFilters => ({
                ...prevFilters,
                filterData: {
                    ...prevFilters.filterData,
                    signDocTypes: [SignDocTypes.FRONTAL_COURSE, SignDocTypes.ONLINE_COURSE], // Maintain course-only filtering
                    signDocs: signDocsOptions
                }
            }));
        }
    }, [signDocsOptions]);

    const handleApprove = useCallback(async (row) => {
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId: row.id,
                expiryDate: null,
                isApproved: true
            });
            gridContext.triggerTableRefresh();
        } catch (error) {
            console.error('Error marking attendance:', error);
        }
    }, [gridContext]);

    const handleRejectClick = useCallback(async (row) => {
        // For courses, no rejection reason needed - just mark as NO_SHOW
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId: row.id,
                expiryDate: null,
                isApproved: false
                // No rejectionReason needed for courses
            });
            gridContext.triggerTableRefresh();
        } catch (error) {
            console.error('Error marking no-show:', error);
        }
    }, [gridContext]);

    const getColumnsCallback = useCallback(async () => {
        return getColumns({ handleApprove, handleRejectClick });
    }, [handleApprove, handleRejectClick]);

    const apiToGetData = useCallback(async ({ paginationModel, filterModel, sortModel }) => {
        const response = await getPendingApprovalsAPI({ paginationModel, filterModel, sortModel, types: [SignDocTypes.FRONTAL_COURSE, SignDocTypes.ONLINE_COURSE] });
        return {
            data: get(response, 'userSignDocs', []),
            count: get(response, 'count', 0)
        };
    }, []);

    return (
        <DataGridWrapper
            getColumns={getColumnsCallback}
            apiToGetData={apiToGetData}
            dataConverter={convertData}
            filterModel={localTableFilters}
            paginationModel={paginationModel}
            adminOrganizations={adminOrganizations} // Pass directly for SignDoc filtering (NOT sent to backend)
        />
    );
};

export default CourseAttendanceTable;
